import { Injectable } from '@angular/core';
import { DropSettings } from '../models/drop-settings';
import { PlanogramDrop } from '../models/layout/planogram-drop';
import { PlanogramLayout } from '../models/layout/planogram-layout';
import { PlanogramProduct } from '../models/layout/planogram-product';
import { PlanogramProductStack } from '../models/layout/planogram-product-stack';
import { PlanogramShelf } from '../models/layout/planogram-shelf';
import { ProductOrientation } from '../models/layout/product-orientation';
import { ProductReference } from '../models/layout/product-reference';
import { LayoutHistoryService } from './layout-history.service';
import { LayoutMutationService } from './layout-mutation.service';
import { LayoutQueryService } from './layout-query.service';
import { LayoutStateService } from './layout-state.service';

/**
 * Façade over the carved-up layout sub-services. Preserves the public API
 * that the rest of the app (components, context-menu, validators) was
 * written against — every method here is a thin delegate to state /
 * history / query / mutation.
 *
 * Internal collaborators should depend on the specific sub-service they
 * need; LayoutService is for external callers that want a single seam.
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  public get layout$() {
    return this.state.layout$;
  }
  public get productsChanged$() {
    return this.state.productsChanged$;
  }
  public get layoutBreakdown$() {
    return this.state.layoutBreakdown$;
  }
  public get layoutDOS$() {
    return this.state.layoutDOS$;
  }
  public get layoutDFacings$() {
    return this.state.layoutDFacings$;
  }
  public get layoutCapacity$() {
    return this.state.layoutCapacity$;
  }

  public get planogramItems(): Map<number, ProductReference> {
    return this.state.planogramItems;
  }
  public set planogramItems(value: Map<number, ProductReference>) {
    this.state.planogramItems = value;
  }

  constructor(
    private state: LayoutStateService,
    private history: LayoutHistoryService,
    private query: LayoutQueryService,
    private mutation: LayoutMutationService
  ) {}

  // ---------- History ----------
  public canUndo(): boolean {
    return this.history.canUndo();
  }

  public canRedo(): boolean {
    return this.history.canRedo();
  }

  public undo() {
    this.history.undo();
  }

  public redo() {
    this.history.redo();
  }

  public clearHistory() {
    this.history.clear();
  }

  // ---------- Queries ----------
  public getDropSettings(index: number): DropSettings | null {
    return this.query.getDropSettings(index);
  }

  public getActualFacingCounts(): Map<number, number> {
    return this.query.getActualFacingCounts();
  }

  public getProductsLinearUsedSpace(): Map<number, number> {
    return this.query.getProductsLinearUsedSpace();
  }

  getProductDOS(productId: number): number {
    return this.query.getProductDOS(productId);
  }

  public getLayoutDOS(): Map<number, number> {
    return this.query.getLayoutDOS();
  }

  getDrop(dropIndex: number): PlanogramDrop | undefined {
    return this.query.getDrop(dropIndex);
  }

  getShelf(dropIndex: number, shelfIndex: number): PlanogramShelf | undefined {
    return this.query.getShelf(dropIndex, shelfIndex);
  }

  getShelves(dropIndex: number): PlanogramShelf[] | undefined {
    return this.query.getShelves(dropIndex);
  }

  getStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number
  ): PlanogramProductStack | undefined {
    return this.query.getStack(dropIndex, shelfIndex, stackIndex);
  }

  getProduct(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ): PlanogramProduct | undefined {
    return this.query.getProduct(dropIndex, shelfIndex, stackIndex, productIndex);
  }

  getCurrentLayout(deepclone: boolean = true): PlanogramLayout | null {
    return this.query.getCurrentLayout(deepclone);
  }

  // ---------- Mutations ----------
  public setDropSettings(index: number, settings: DropSettings) {
    this.mutation.setDropSettings(index, settings);
  }

  public setLayout(layout: PlanogramLayout) {
    this.mutation.setLayout(layout);
  }

  public removeProducts(products: number[]) {
    this.mutation.removeProducts(products);
  }

  public importProducts(products: ProductReference[]) {
    this.mutation.importProducts(products);
  }

  public refreshProducts(products: ProductReference[], fields: string[]): boolean {
    return this.mutation.refreshProducts(products, fields);
  }

  removeProductFromLayout(productId: number) {
    this.mutation.removeProductFromLayout(productId);
  }

  flagProductCaptureRequired(productId: number) {
    this.mutation.flagProductCaptureRequired(productId);
  }

  bulkRemoveProductsFromLayout(productIds: number[]) {
    this.mutation.bulkRemoveProductsFromLayout(productIds);
  }

  // Drop Operations
  addDrop(index: number) {
    this.mutation.addDrop(index);
  }

  addDropBefore(index: number) {
    this.mutation.addDropBefore(index);
  }

  addDropAfter(index: number) {
    this.mutation.addDropAfter(index);
  }

  removeDrop(index: number) {
    this.mutation.removeDrop(index);
  }

  clearDrop(dropIndex: number) {
    this.mutation.clearDrop(dropIndex);
  }

  copyDrop(dropIndex: number) {
    this.mutation.copyDrop(dropIndex);
  }

  pasteDropBefore(dropIndex: number) {
    this.mutation.pasteDropBefore(dropIndex);
  }

  pasteDropAfter(dropIndex: number) {
    this.mutation.pasteDropAfter(dropIndex);
  }

  // Shelf Operations
  addShelf(
    dropIndex: number,
    shelfIndex: number,
    newShelf: PlanogramShelf | null = null,
    skipValidation: boolean = false
  ) {
    this.mutation.addShelf(dropIndex, shelfIndex, newShelf, skipValidation);
  }

  removeShelf(dropIndex: number, shelfIndex: number) {
    this.mutation.removeShelf(dropIndex, shelfIndex);
  }

  moveShelf(
    sourceDropIndex: number,
    sourceShelfIndex: number,
    targetDropIndex: number,
    targetShelfIndex: number
  ) {
    this.mutation.moveShelf(
      sourceDropIndex,
      sourceShelfIndex,
      targetDropIndex,
      targetShelfIndex
    );
  }

  clearShelf(dropIndex: number, shelfIndex: number) {
    this.mutation.clearShelf(dropIndex, shelfIndex);
  }

  createShelf(dropIndex: number) {
    this.mutation.createShelf(dropIndex);
  }

  // Product Operations
  addProductToStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    product: PlanogramProduct,
    skipValidation: boolean = false
  ) {
    this.mutation.addProductToStack(
      dropIndex,
      shelfIndex,
      stackIndex,
      productIndex,
      product,
      skipValidation
    );
  }

  addProductAsNewStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    product: PlanogramProduct,
    skipValidation: boolean = false
  ) {
    this.mutation.addProductAsNewStack(
      dropIndex,
      shelfIndex,
      stackIndex,
      product,
      skipValidation
    );
  }

  removeProduct(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutation.removeProduct(dropIndex, shelfIndex, stackIndex, productIndex);
  }

  moveProduct(
    sourceDropIndex: number,
    sourceShelfIndex: number,
    sourceStackIndex: number,
    sourceProductIndex: number,
    targetDropIndex: number,
    targetShelfIndex: number,
    targetStackIndex: number | null,
    asNewStack: boolean = false,
    targetProductIndex: number = 0
  ) {
    this.mutation.moveProduct(
      sourceDropIndex,
      sourceShelfIndex,
      sourceStackIndex,
      sourceProductIndex,
      targetDropIndex,
      targetShelfIndex,
      targetStackIndex,
      asNewStack,
      targetProductIndex
    );
  }

  // Facing Operations
  addHFacing(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutation.addHFacing(dropIndex, shelfIndex, stackIndex, productIndex);
  }

  removeHFacing(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutation.removeHFacing(dropIndex, shelfIndex, stackIndex, productIndex);
  }

  addVFacing(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutation.addVFacing(dropIndex, shelfIndex, stackIndex, productIndex);
  }

  removeVFacing(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutation.removeVFacing(dropIndex, shelfIndex, stackIndex, productIndex);
  }

  changeProductOrientation(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    orientation: ProductOrientation
  ) {
    this.mutation.changeProductOrientation(dropIndex, shelfIndex, stackIndex, productIndex, orientation);
  }

  duplicateStack(dropIndex: number, shelfIndex: number, stackIndex: number) {
    this.mutation.duplicateStack(dropIndex, shelfIndex, stackIndex);
  }

  // Stack order operations
  moveProductUpInStack(d: number, s: number, k: number, p: number) {
    this.mutation.moveProductUpInStack(d, s, k, p);
  }

  moveProductDownInStack(d: number, s: number, k: number, p: number) {
    this.mutation.moveProductDownInStack(d, s, k, p);
  }

  moveProductToTopOfStack(d: number, s: number, k: number, p: number) {
    this.mutation.moveProductToTopOfStack(d, s, k, p);
  }

  moveProductToBottomOfStack(d: number, s: number, k: number, p: number) {
    this.mutation.moveProductToBottomOfStack(d, s, k, p);
  }

  // Removal operations
  removeStack(dropIndex: number, shelfIndex: number, stackIndex: number) {
    this.mutation.removeStack(dropIndex, shelfIndex, stackIndex);
  }

  clearStackAbove(d: number, s: number, k: number, p: number) {
    this.mutation.clearStackAbove(d, s, k, p);
  }

  clearStackBelow(d: number, s: number, k: number, p: number) {
    this.mutation.clearStackBelow(d, s, k, p);
  }

  // Clipboard operations
  pasteProductBefore(d: number, s: number, k: number) {
    this.mutation.pasteProductBefore(d, s, k);
  }

  pasteProductAfter(d: number, s: number, k: number) {
    this.mutation.pasteProductAfter(d, s, k);
  }

  pasteShelf(dropIndex: number) {
    this.mutation.pasteShelf(dropIndex);
  }

  pasteShelfAbove(dropIndex: number, shelfIndex: number) {
    this.mutation.pasteShelfAbove(dropIndex, shelfIndex);
  }

  pasteShelfBelow(dropIndex: number, shelfIndex: number) {
    this.mutation.pasteShelfBelow(dropIndex, shelfIndex);
  }

  pasteProductAbove(d: number, s: number, k: number, p: number) {
    this.mutation.pasteProductAbove(d, s, k, p);
  }

  pasteProductBelow(d: number, s: number, k: number, p: number) {
    this.mutation.pasteProductBelow(d, s, k, p);
  }

  pasteProductBeforeStack(d: number, s: number, k: number) {
    this.mutation.pasteProductBeforeStack(d, s, k);
  }

  pasteProductAfterStack(d: number, s: number, k: number) {
    this.mutation.pasteProductAfterStack(d, s, k);
  }

  pasteStackBefore(dropIndex: number, shelfIndex: number, stackIndex: number) {
    this.mutation.pasteStackBefore(dropIndex, shelfIndex, stackIndex);
  }

  pasteStackAfter(dropIndex: number, shelfIndex: number, stackIndex: number) {
    this.mutation.pasteStackAfter(dropIndex, shelfIndex, stackIndex);
  }

  pasteProductProperties(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ) {
    this.mutation.pasteProductProperties(dropIndex, shelfIndex, stackIndex, productIndex);
  }

  public setProductDragging(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    dragging: boolean
  ) {
    this.mutation.setProductDragging(dropIndex, shelfIndex, stackIndex, productIndex, dragging);
  }

  public setShelfDragging(
    dropIndex: number,
    shelfIndex: number,
    dragging: boolean
  ) {
    this.mutation.setShelfDragging(dropIndex, shelfIndex, dragging);
  }

  // Floating shelf operations
  addProductToFloatingShelf(productId: number) {
    this.mutation.addProductToFloatingShelf(productId);
  }

  removeProductFromFloatingShelf(productId: number) {
    this.mutation.removeProductFromFloatingShelf(productId);
  }

  setFloatingShelfCapacity(productId: number, capacity: number) {
    this.mutation.setFloatingShelfCapacity(productId, capacity);
  }

  clearLayout() {
    this.mutation.clearLayout();
  }
}
